"use client"

import { useEffect, useRef, useState } from "react"

type Props = {
  end: number
  start?: number
  duration?: number // ms
  className?: string
  formatter?: (n: number) => string
}

export default function CountUp({ end, start = 0, duration = 1200, className, formatter }: Props) {
  const [value, setValue] = useState<number>(start)
  const [hasStarted, setHasStarted] = useState(false)
  const ref = useRef<HTMLSpanElement | null>(null)

  // Start when in view
  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setHasStarted(true)
          observer.disconnect()
        }
      },
      { threshold: 0.2 }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Animate value
  useEffect(() => {
    if (!hasStarted) return

    let raf = 0
    const from = start
    const to = Number.isFinite(end) ? end : 0
    const d = Math.max(200, duration)
    const t0 = performance.now()

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / d)
      const eased = easeOutCubic(t)
      const current = Math.round(from + (to - from) * eased)
      setValue(current)
      if (t < 1) raf = requestAnimationFrame(step)
    }

    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [hasStarted, start, end, duration])

  const display = formatter ? formatter(value) : value.toLocaleString()
  return (
    <span ref={ref} className={className}>{display}</span>
  )
}
